interface Props {
  variant: 'hero' | 'footer';
}

/**
 * Purely decorative animated flight-path layer.
 * Wrap the parent section in `relative overflow-hidden` before using this.
 */
export default function FlightPathAnimation({ variant }: Props) {
  const isHero = variant === 'hero';

  /* ── colour tokens ─────────────────────────────────────────── */
  const planeColor  = isHero ? '#14b8a6' : '#5eead4';   /* teal-500 / teal-300 */
  const lineColor   = isHero ? 'rgba(20,184,166,0.12)'  : 'rgba(94,234,212,0.14)';
  const dotColor    = isHero ? 'rgba(20,184,166,0.18)'  : 'rgba(94,234,212,0.20)';

  /* ── hero route lines (left → right, angled upward) ─────────── */
  const heroLines = [
    { top: '32%', rotate: '-9deg',  delay: '0s',   dur: '22s' },
    { top: '58%', rotate: '-5deg',  delay: '4s',   dur: '28s' },
    { top: '78%', rotate: '-3deg',  delay: '10s',  dur: '20s' },
  ];

  /* ── footer route lines (right → left, slightly angled) ─────── */
  const footerLines = [
    { top: '28%', rotate: '6deg',   delay: '0s',   dur: '25s' },
    { top: '62%', rotate: '3deg',   delay: '6s',   dur: '18s' },
  ];

  const lines = isHero ? heroLines : footerLines;

  /* ── planes for hero ─────────────────────────────────────────── */
  const heroPlanes = [
    {
      top: '30%', size: 28, rotate: '-20deg', opacity: 0.18,
      animation: 'heroFly1 22s linear infinite 0s',
    },
    {
      top: '55%', size: 20, rotate: '-14deg', opacity: 0.13,
      animation: 'heroFly2 16s linear infinite 8s',
    },
    {
      top: '72%', size: 15, rotate: '-10deg', opacity: 0.10,
      animation: 'heroFly3 12s linear infinite 3s',
    },
  ];

  /* ── planes for footer ───────────────────────────────────────── */
  const footerPlanes = [
    {
      top: '26%', size: 26, rotate: '160deg', opacity: 0.20,
      animation: 'footerFly1 20s linear infinite 0s',
    },
    {
      top: '60%', size: 18, rotate: '165deg', opacity: 0.14,
      animation: 'footerFly2 14s linear infinite 7s',
    },
  ];

  const planes = isHero ? heroPlanes : footerPlanes;

  /* ── waypoint dots scattered along each route ────────────────── */
  const heroDots = [
    { top: '31%', left: '20%' }, { top: '29%', left: '45%' }, { top: '27%', left: '70%' },
    { top: '57%', left: '15%' }, { top: '55%', left: '55%' }, { top: '53%', left: '82%' },
  ];
  const footerDots = [
    { top: '29%', left: '25%' }, { top: '27%', left: '60%' }, { top: '25%', left: '85%' },
    { top: '63%', left: '10%' }, { top: '61%', left: '42%' }, { top: '59%', left: '74%' },
  ];
  const dots = isHero ? heroDots : footerDots;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">

      {/* ── dashed route lines ──────────────────────────────────── */}
      {lines.map((l, i) => (
        <div
          key={`line-${i}`}
          className="absolute left-0 w-[220%] -translate-x-[10%]"
          style={{
            top: l.top,
            height: 0,
            borderTop: `1.5px dashed ${lineColor}`,
            transform: `rotate(${l.rotate})`,
            transformOrigin: 'left center',
          }}
        />
      ))}

      {/* ── animated planes ─────────────────────────────────────── */}
      {planes.map((p, i) => (
        <span
          key={`plane-${i}`}
          className="absolute left-0"
          style={{
            top: p.top,
            color: planeColor,
            opacity: p.opacity,
            fontSize: p.size,
            lineHeight: 1,
            animation: p.animation,
          }}
        >
          {/* inner element carries only the rotation so animation translate stays clean */}
          <i
            className="ri-plane-fill block"
            style={{ transform: `rotate(${p.rotate})` }}
          />
        </span>
      ))}

      {/* ── floating parked plane (hero only, top-right corner) ──── */}
      {isHero && (
        <span
          className="absolute right-[8%] top-[12%]"
          style={{
            color: planeColor,
            opacity: 0.12,
            fontSize: 38,
            lineHeight: 1,
            animation: 'floatPlane 5s ease-in-out infinite',
          }}
        >
          <i className="ri-plane-line block" />
        </span>
      )}

      {/* ── waypoint dots ───────────────────────────────────────── */}
      {dots.map((d, i) => (
        <span
          key={`dot-${i}`}
          className="absolute block rounded-full"
          style={{
            top: d.top,
            left: d.left,
            width: 5,
            height: 5,
            background: dotColor,
          }}
        />
      ))}
    </div>
  );
}
